import { useAction } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button, PlusCircleIcon } from '@blade/ui-kit'

import { COMMENT_ITEM_AVATAR_SIZE } from './consts'

import type { Comment } from '../../model'
import type { CommentsModel } from '@root/features/comment'

type CommentItemFooterProps = {
  comment: Comment.Comment
  depth?: number
  hasMoreReplies: boolean
  onExpandClick: () => void
  model: CommentsModel
}

export const CommentItemFooter = ({
  model,
  hasMoreReplies,
  comment,
  depth,
  onExpandClick,
}: CommentItemFooterProps) => {
  const { t } = useTranslation()

  const loadRepliesThread = useAction(model.loadRepliesThreadAction)

  const handleLoadCommentReplies = () => loadRepliesThread()

  const repliesLevel0Total = comment.replies.length
  const totalHidden = repliesLevel0Total - (depth! + 1)
  const hasLevel0Replies = depth !== undefined && totalHidden > 0

  if (hasMoreReplies) {
    return (
      <Root>
        <Blank />
        <ButtonShowReplies
          size="xxs"
          type="button"
          variant="ghost"
          onClick={handleLoadCommentReplies}
        >
          <PlusCircleIcon size={16} />
          {t('comment.comments_load_all', {
            count: comment.totalReplies,
          })}
        </ButtonShowReplies>
      </Root>
    )
  }

  if (hasLevel0Replies) {
    const totalHiddenReplies = comment.replies.reduce(
      (acc, it, index) => acc + (index > depth ? it.totalReplies + 1 : 0),
      0,
    )

    return (
      <Root>
        <Blank />
        <ButtonShowReplies
          size="xxs"
          type="button"
          variant="ghost"
          onClick={onExpandClick}
        >
          <PlusCircleIcon size={16} />
          {t('comment.comments_all', { count: totalHiddenReplies })}
        </ButtonShowReplies>
      </Root>
    )
  }
}

const Root = styled.footer`
  display: flex;
  align-items: center;

  gap: 10px;
  margin-bottom: 12px;
`

const ButtonShowReplies = styled(Button)`
  color: var(--text-9);
  background: transparent;

  display: flex;
  align-items: center;

  padding: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  gap: 6px;

  &:hover {
    background: transparent;
  }

  svg {
    margin-top: -1px;
  }
`

const Blank = styled.aside`
  width: ${COMMENT_ITEM_AVATAR_SIZE}px;
`
